/**
 * WelcomeEngagementModal — one-time modal shown the FIRST time a
 * signed-in user lands on the Dashboard post-signup. Explains the two
 * incomplete signals (profile + FreedrIQ) in a warmer, opinionated
 * frame than the persistent banner. Dismissed forever via localStorage
 * so no repeat.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { ClipboardList, Sparkles, Timer } from 'lucide-react';

const LS_KEY = 'fdacademy:welcome_modal_seen';

export default function WelcomeEngagementModal() {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);

  useEffect(() => {
    if (!user) return undefined;
    try { if (localStorage.getItem(LS_KEY)) return undefined; } catch (_) {}
    let cancel = false;
    axios.get(`${API}/user/engagement-status`).then(({ data }) => {
      if (cancel) return;
      setState(data);
      if (data?.should_prompt) setOpen(true);
      else { try { localStorage.setItem(LS_KEY, '1'); } catch (_) {} }
    }).catch(() => {});
    return () => { cancel = true; };
  }, [API, user]);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem(LS_KEY, '1'); } catch (_) {}
  };
  const goProfile = () => { close(); window.location.href = '/portal?tab=profile'; };
  const goFreedrIQ = () => { close(); navigate('/freedriq?tier=free&phase=pre'); };

  if (!state) return null;
  const profileIncomplete  = !state.profile?.complete;
  const freedriqIncomplete = !state.freedriq?.started;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-lg" data-testid="welcome-engagement-modal">
        <div className="eyebrow text-primary inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Welcome to Freeze Dry Academy
        </div>
        <DialogTitle className="font-heading text-2xl font-extrabold mt-1">
          Two quick steps before you dive in
        </DialogTitle>
        <DialogDescription className="mt-1">
          Your profile + a 10-minute FreedrIQ baseline lets us tailor every lesson,
          calculator, and event invitation to you.
        </DialogDescription>

        <div className="mt-4 space-y-3">
          {profileIncomplete && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 flex items-start gap-3">
              <ClipboardList className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold">Finish your profile</div>
                <div className="text-xs text-muted-foreground">
                  Machine, region, experience — powers recommendations + events.
                </div>
              </div>
              <Button size="sm" onClick={goProfile} data-testid="welcome-modal-profile-cta" className="bg-amber-500 text-navy hover:bg-amber-500/90">
                Start
              </Button>
            </div>
          )}
          {freedriqIncomplete && (
            <div className="rounded-xl border border-primary/40 bg-primary/5 p-3 flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-primary mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold">Take the FreedrIQ entry assessment</div>
                <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <Timer className="w-3 h-3" /> ~10 minutes · establishes your baseline score
                </div>
              </div>
              <Button size="sm" onClick={goFreedrIQ} data-testid="welcome-modal-freedriq-cta">
                Start
              </Button>
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={close} data-testid="welcome-modal-skip-btn">
            Do it later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
